import { useEffect, useState } from 'react';
import type { OrderFormData, CustomerData, Vendor, SelectedItem, VendorProduct } from '../../types';
import { isRebateSubCategory } from '../../../../../../../utils/vendorCategoryUtils';

interface ContractInfo {
  id: string;
  vendorName: string;
  vendorCategory: string;
  productName: string;
  category: string; // 진행구분
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  formData: OrderFormData;
  setFormData: (data: OrderFormData) => void;
  customer: CustomerData;
  customerId: string;
  selectedVendor: Vendor | null;
  onVendorSelectClick: () => void;
  onVendorRemoveClick: () => void;
  onSubmit: (items: SelectedItem[], attachedContracts?: ContractInfo[]) => void;
}

export default function OrderFormModal({
  isOpen,
  onClose,
  formData,
  setFormData,
  customer,
  customerId,
  selectedVendor,
  onVendorSelectClick,
  onVendorRemoveClick,
  onSubmit
}: Props) {
  if (!isOpen) return null;

  const [vendorProducts, setVendorProducts] = useState<VendorProduct[]>([]);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // 고객 계약 현황 (참고 정보 첨부용)
  const [allContracts, setAllContracts] = useState<ContractInfo[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<Set<string>>(new Set());

  // 리베이트 기반 업체 판별 (혼수업체, 웨딩홀 등)
  const isRebate = selectedVendor ? isRebateSubCategory(selectedVendor.category || '') : false;

  // 고객의 전체 계약 현황 조회
  useEffect(() => {
    if (!customerId) return;
    const fetchContracts = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`https://api.wedmin.biz/customer/${customerId}/detail?tab=PRODUCTS`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await res.json();
        if (result.status && result.data && Array.isArray(result.data.tabData)) {
          setAllContracts(result.data.tabData.map((p: any) => ({
            id: String(p.customerProductItemIdx),
            vendorName: p.vendor?.name || '-',
            vendorCategory: p.vendor?.category?.name || '-',
            productName: p.productName || p.product?.name || '-',
            category: p.categoryName || '-',
          })));
        }
      } catch (e) { console.error(e); }
    };
    fetchContracts();
  }, [customerId]);

  // 발주 대상 업체의 계약은 제외한 목록
  const otherContracts = allContracts.filter(
    (c) => c.vendorName !== selectedVendor?.name
  );

  const handleContractToggle = (id: string) => {
    setSelectedContractIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSelectAllContracts = () => {
    if (selectedContractIds.size === otherContracts.length) {
      setSelectedContractIds(new Set());
    } else {
      setSelectedContractIds(new Set(otherContracts.map((c) => c.id)));
    }
  };

  useEffect(() => {
    if (selectedVendor?.vendorIdx) {
      fetchVendorProducts(selectedVendor.vendorIdx);
    } else {
      setVendorProducts([]);
      setSelectedItems([]);
    }
  }, [selectedVendor]);

  // 선택된 상품이 변경될 때 총 계약금액 자동 업데이트 (선택 사항)
  useEffect(() => {
    const total = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    // 금액 필드가 비어있거나 0일 때만 자동 채움 (사용자가 수동 수정한 경우 덮어쓰지 않음)
    // 필요에 따라 항상 덮어쓰도록 수정 가능
    if (total > 0) {
      setFormData({ ...formData, amount: total.toString() });
    }
  }, [selectedItems]);

  const fetchVendorProducts = async (vendorIdx: number) => {
    setIsLoadingProducts(true);
    try {
      const token = localStorage.getItem('accessToken');
      // API 호출: 해당 업체의 상품 조회
      const response = await fetch(`https://api.wedmin.biz/product?partnerVendorIdx=${vendorIdx}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      // API 응답 구조에 따라 result.items 또는 result.data.items 확인 필요
      // 여기서는 result.items로 가정 (일관성을 위해)
      if (result.items) {
        setVendorProducts(result.items);
      } else if (result.data && result.data.items) {
        setVendorProducts(result.data.items);
      }
    } catch (error) {
      console.error('Fetch Products Error:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductToggle = (product: VendorProduct) => {
    const exists = selectedItems.find(item => item.productIdx === product.productIdx);
    if (exists) {
      setSelectedItems(prev => prev.filter(item => item.productIdx !== product.productIdx));
    } else {
      setSelectedItems(prev => [...prev, {
        productIdx: product.productIdx,
        name: product.name,
        price: product.salesPrice,
        quantity: 1
      }]);
    }
  };

  const handleQuantityChange = (productIdx: number, newQty: number) => {
    if (newQty < 1) return;
    setSelectedItems(prev => prev.map(item => 
      item.productIdx === productIdx ? { ...item, quantity: newQty } : item
    ));
  };

  const handleSubmit = () => {
    // ✅ 유효성 검사 추가
    if (!selectedVendor) {
      alert("발주할 업체를 선택해주세요.");
      return;
    }
    if (selectedItems.length === 0) {
      alert("발주할 상품을 최소 1개 이상 선택해주세요.");
      return;
    }

    const attachedContracts = otherContracts.filter((c) => selectedContractIds.has(c.id));
    onSubmit(selectedItems, attachedContracts.length > 0 ? attachedContracts : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">발주서 작성</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* 고객 정보 (자동 입력) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">고객 정보</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">고객명</label>
                  <input type="text" value={customer.applicationName} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">연락처</label>
                  <input type="text" value={customer.groomContact} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">본식일</label>
                  <input type="text" value={customer.weddingDateTime.split(' ')[0]} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">예식장</label>
                  <input type="text" value={customer.weddingLocation} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                </div>
              </div>
            </div>
          </div>

          {/* 업체 정보 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">업체 정보</h3>
            
            {/* 업체 선택 버튼 */}
            {!selectedVendor && (
              <div className="mb-4">
                <button
                  onClick={onVendorSelectClick}
                  className="w-full px-4 py-3 border-2 border-dashed border-green-300 rounded-lg text-green-600 hover:bg-green-50 transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-building-line text-xl"></i>
                  <span className="font-medium">업체 검색 및 선택</span>
                </button>
              </div>
            )}

            {/* 선택된 업체 정보 표시 */}
            {selectedVendor && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-sm font-semibold text-green-900">선택된 업체: {selectedVendor.name}</span>
                  </div>
                  <button
                    onClick={onVendorRemoveClick}
                    className="text-green-600 hover:text-green-800 text-sm cursor-pointer"
                  >
                    변경 / 해제
                  </button>
                </div>
                
                {/* ✅ 상품 선택 영역 (업체가 선택되었을 때만 표시) */}
                <div className="mt-4 bg-white rounded border border-green-100 p-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">발주할 상품 선택 (필수)</h4>
                  {isLoadingProducts ? (
                    <p className="text-sm text-gray-500">상품 목록 로딩 중...</p>
                  ) : vendorProducts.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {vendorProducts.map(prod => {
                        const isSelected = selectedItems.some(i => i.productIdx === prod.productIdx);
                        const selectedItem = selectedItems.find(i => i.productIdx === prod.productIdx);
                        
                        return (
                          <div key={prod.productIdx} className={`flex items-center justify-between p-2 rounded border transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                            <div className="flex items-center gap-3">
                              <input 
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleProductToggle(prod)}
                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                              />
                              <span className="text-sm text-gray-800">{prod.name}</span>
                              {!isRebate && <span className="text-xs text-gray-500">({prod.salesPrice.toLocaleString()}원)</span>}
                            </div>
                            
                            {isSelected && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">수량:</span>
                                <input 
                                  type="number" 
                                  min="1"
                                  value={selectedItem?.quantity || 1}
                                  onChange={(e) => handleQuantityChange(prod.productIdx, parseInt(e.target.value))}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">이 업체에 등록된 상품이 없습니다.</p>
                  )}
                </div>
              </div>
            )}

            {/* 수동 입력 필드 (선택된 업체 정보 보여주기용) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">업체명</label>
                <input type="text" value={formData.vendor} onChange={(e) => setFormData({...formData, vendor: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">담당자</label>
                <input type="text" value={formData.vendorManager} onChange={(e) => setFormData({...formData, vendorManager: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">업체 연락처</label>
                <input type="text" value={formData.vendorPhone} onChange={(e) => setFormData({...formData, vendorPhone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">서비스 유형</label>
                <input type="text" value={selectedVendor?.category || ''} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
              </div>
            </div>
          </div>

          {/* 금액 정보 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">금액 정보</h3>

            {isRebate ? (
              /* ===== 혼수업체: 계약금/리베이트 기반 ===== */
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <span className="text-sm font-medium text-purple-700"><i className="ri-gift-line mr-1"></i>리베이트 계약 발주</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">계약금</label>
                    <input type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">리베이트 (%)</label>
                    <input type="text" value={formData.commission} onChange={(e) => setFormData({...formData, commission: e.target.value})} placeholder="예: 15%" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">리베이트 금액</label>
                    <input type="text" value={
                      formData.amount && formData.commission
                        ? Math.round(Number(formData.amount) * parseFloat(formData.commission) / 100).toLocaleString() + '원'
                        : '0원'
                    } readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">입금예정일</label>
                    <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>
            ) : (
              /* ===== 일반 업체: 기존 금액 정보 ===== */
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">계약금액 (자동합산)</label>
                  <input type="text" value={Number(formData.amount).toLocaleString()} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">수수료율</label>
                  <input type="text" value={formData.commission} onChange={(e) => setFormData({...formData, commission: e.target.value})} placeholder="예: 15%" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">결제조건</label>
                  <select value={formData.paymentTerms || '일시불'} onChange={(e) => setFormData({...formData, paymentTerms: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8">
                    <option>일시불</option>
                    <option>계약금 50% + 잔금 50%</option>
                    <option>계약금 30% + 잔금 70%</option>
                    <option>계약금 30% + 중도금 30% + 잔금 40%</option>
                    <option>계약금 10% + 잔금 90%</option>
                    <option>선불 100%</option>
                    <option>후불 100%</option>
                    <option>협의 후 결정</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">납기일</label>
                  <input type="date" value={formData.dueDate} onChange={(e) => setFormData({...formData, dueDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            )}
          </div>

          {/* 발주 기본 정보 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">발주 기본 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">발주일 <span className="text-red-500">*</span></label>
                <input type="date" value={formData.orderDate} onChange={(e) => setFormData({...formData, orderDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">예식일</label>
                <input type="date" value={formData.weddingDate} onChange={(e) => setFormData({...formData, weddingDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* 고객 계약 현황 첨부 (참고 정보) */}
          {selectedVendor && otherContracts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <i className="ri-attachment-line text-gray-400"></i>
                  고객 계약 현황 첨부
                  <span className="text-xs font-normal text-gray-400">(선택)</span>
                </h3>
                <button
                  type="button"
                  onClick={handleSelectAllContracts}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {selectedContractIds.size === otherContracts.length ? '전체 해제' : '전체 선택'}
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {otherContracts.map((c) => {
                  const checked = selectedContractIds.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleContractToggle(c.id)}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                        {c.vendorCategory}
                      </span>
                      <span className="text-sm text-gray-800 font-medium">{c.vendorName}</span>
                      <span className="text-sm text-gray-500">—</span>
                      <span className="text-sm text-gray-600">{c.productName}</span>
                      {c.category && c.category !== '-' && (
                        <span className="text-xs text-gray-400">({c.category})</span>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1.5 text-xs text-gray-400">선택한 항목이 발주서에 참고 정보로 포함됩니다.</p>
            </div>
          )}

          {/* 메모 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">메모</h3>
            <textarea
              value={formData.remark || ''}
              onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
              rows={3}
              placeholder="업체에게 전달할 메모를 입력하세요."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
            />
          </div>

        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
          <button 
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            취소
          </button>
          <button 
            onClick={handleSubmit}
            // ✅ 선택된 업체나 상품이 없으면 버튼을 비활성화하여 시각적으로 피드백 제공
            disabled={!selectedVendor || selectedItems.length === 0}
            className={`px-6 py-2 rounded-lg font-medium whitespace-nowrap cursor-pointer transition-colors ${
              selectedVendor && selectedItems.length > 0 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            완료
          </button>
        </div>
      </div>
    </div>
  );
}
